// Panele: plecak (przeciąganie między slotami i zakładanie), dziennik zadań. Każda akcja idzie przez serwer.
import { api, ApiError } from '../net/api.js';
import { haptic } from '../platform/native.js';
import { h, toast, ui } from './dom.js';

interface Inst { id: string; code: string; name: string; rarity: string; slot: string; quantity: number; durability: number; max_durability: number; weight_g: number }
interface Container { id: number; slots: number; items: Inst[] }
interface Inventory { inventory: Container; equipment: Container; storage: Container; capacity_g: number }

const EQUIP: [string, string][] = [['head', 'Hełm'], ['face', 'Maska'], ['vest', 'Kamizelka'], ['jacket', 'Kurtka'],
  ['armor', 'Pancerz'], ['hands', 'Rękawice'], ['legs', 'Spodnie'], ['feet', 'Buty'], ['back', 'Plecak'],
  ['primary', 'Broń główna'], ['secondary', 'Dodatkowa'], ['melee', 'Biała'], ['acc1', 'Akcesorium'], ['acc2', 'Akcesorium']];

function panel(title: string, onClose: () => void, ...body: Node[]): HTMLElement {
  document.querySelector('.panel')?.remove();
  const close = h('button', { class: 'close', 'aria-label': 'Zamknij' }, '✕');
  close.onclick = onClose;
  const p = h('div', { class: 'panel' }, h('header', {}, h('h2', {}, title), close), ...body);
  ui().append(p);
  return p;
}

export function closePanel(): boolean {
  const p = document.querySelector('.panel');
  p?.remove();
  return !!p;
}

export async function openInventory(charId: number): Promise<void> {
  let inv: Inventory;
  try { inv = await api<Inventory>(`/items/inventory/${charId}/`); } catch { toast('Nie udało się wczytać plecaka', 'bad'); return; }
  let dragged: Inst | null = null;
  const slotEl = (container: Container, slot: string, label = '') => {
    const it = container.items.find((i) => i.slot === slot);
    const el = h('div', { class: `slot ${it ? `r-${it.rarity}` : 'empty'}`, 'data-slot': slot }, label && !it ? h('small', {}, label) : '');
    if (it) {
      el.append(h('span', { class: 'name' }, it.name));
      if (it.quantity > 1) el.append(h('span', { class: 'qty' }, String(it.quantity)));
      if (it.max_durability) el.append(h('i', { class: 'dur', style: `width:${(it.durability / it.max_durability) * 100}%` }));
      el.draggable = true;
      el.addEventListener('pointerdown', () => { dragged = it; el.classList.add('picked'); haptic('light'); });
    }
    el.addEventListener('pointerup', async () => {
      if (!dragged || dragged.slot === slot && container.items.includes(dragged)) { dragged = null; render(); return; }
      const item = dragged;
      dragged = null;
      try {
        await api(`/items/inventory/${charId}/action/`, { body: { action: 'move', item: item.id, to_container: container.id, to_slot: slot } });
        inv = await api<Inventory>(`/items/inventory/${charId}/`);
      } catch (ex) { toast(ex instanceof ApiError ? ex.message : 'Błąd', 'bad'); }
      render();
    });
    return el;
  };
  const body = h('div', { class: 'inv' });
  const render = () => {
    const weight = inv.inventory.items.reduce((s, i) => s + i.quantity * i.weight_g, 0);
    body.replaceChildren(
      h('div', { class: 'equip' }, ...EQUIP.map(([s, l]) => slotEl(inv.equipment, s, l))),
      h('div', { class: 'bag' },
        h('div', { class: 'weight' }, `Waga: ${(weight / 1000).toFixed(1)} / ${(inv.capacity_g / 1000).toFixed(1)} kg`),
        h('div', { class: 'grid' }, ...Array.from({ length: inv.inventory.slots }, (_, i) => slotEl(inv.inventory, String(i))))));
  };
  render();
  panel('Plecak', closePanel, body, h('p', { class: 'hint' }, 'Przytrzymaj przedmiot i upuść na innym slocie, by przenieść lub założyć.'));
}

export async function openQuests(charId: number): Promise<void> {
  interface Q { code: string; name: string; kind: string; state: string; stage_title: string; stage_description: string; objectives: { description: string; count: number; have: number }[] }
  let list: Q[] = [];
  try { list = await api<Q[]>(`/quests/log/${charId}/`); } catch { /* pusta lista */ }
  const items = list.length ? list.map((q) => h('article', { class: `quest ${q.state}` },
    h('h3', {}, q.name, h('small', {}, q.kind === 'main' ? ' · fabuła' : '')),
    h('b', {}, q.stage_title), h('p', {}, q.stage_description),
    h('ul', {}, ...q.objectives.map((o) => h('li', { class: o.have >= o.count ? 'done' : '' }, `${o.description} (${o.have}/${o.count})`)))))
    : [h('p', {}, 'Brak zadań. Porozmawiaj z Haliną w schronie.')];
  panel('Zadania', closePanel, ...items);
}
